// Centralized asset loading utilities using Vite's import.meta.glob
// This avoids code duplication across components that need to dynamically load assets
//
// Usage:
//   import { getAsset } from "@/helpers/assetsUtils";
//   const image = getAsset('partners', 'yachtpool.png');
//   const icon = getAsset('homeSvg', 'secure.svg');

// ============================================================================
// Asset Glob Imports
// ============================================================================

const boatTypeAssets = import.meta.glob('@/assets/boatTypes/*.svg', { eager: true, import: 'default' }) as Record<string, string>;
const partnerPageAssets = import.meta.glob('@/assets/partners/page/*.{png,jpg}', { eager: true, import: 'default' }) as Record<string, string>;
const partnerLogoAssets = import.meta.glob('@/assets/partners/*.png', { eager: true, import: 'default' }) as Record<string, string>;
const milestoneAssets = import.meta.glob('@/assets/about/milestone/*.{png,jpg}', { eager: true, import: 'default' }) as Record<string, string>;
const aboutBoatAssets = import.meta.glob('@/assets/about/boat-*.png', { eager: true, import: 'default' }) as Record<string, string>;
const homeSvgAssets = import.meta.glob('@/assets/home/svg/*.svg', { eager: true, import: 'default' }) as Record<string, string>;
const testimonialAssets = import.meta.glob('@/assets/testimonial-faces/*.jpg', { eager: true, import: 'default' }) as Record<string, string>;
const iconSvgAssets = import.meta.glob('@/assets/icons/svg/*.svg', { eager: true, import: 'default' }) as Record<string, string>;
const imageAssets = import.meta.glob('@/assets/images/*.{png,jpg,svg}', { eager: true, import: 'default' }) as Record<string, string>;
const paymentAssets = import.meta.glob('@/assets/images/payment/*.svg', { eager: true, import: 'default' }) as Record<string, string>;

// ============================================================================
// Asset Category Configuration
// ============================================================================

export type AssetCategory =
    | 'boatTypes'      // /assets/boatTypes/*.svg
    | 'partners'       // /assets/partners/page/*.png
    | 'partnerLogos'   // /assets/partners/*.png
    | 'milestones'     // /assets/about/milestone/*.{png,jpg}
    | 'aboutBoats'     // /assets/about/boat-*.png
    | 'homeSvg'        // /assets/home/svg/*.svg
    | 'testimonials'   // /assets/testimonial-faces/*.jpg
    | 'icons'          // /assets/icons/svg/*.svg
    | 'images'         // /assets/images/*.{png,jpg,svg}
    | 'payment';       // /assets/images/payment/*.svg

const assetMaps: Record<AssetCategory, { assets: Record<string, string>; basePath: string }> = {
    boatTypes:    { assets: boatTypeAssets,    basePath: '/assets/boatTypes/' },
    partners:     { assets: partnerPageAssets, basePath: '/assets/partners/page/' },
    partnerLogos: { assets: partnerLogoAssets, basePath: '/assets/partners/' },
    milestones:   { assets: milestoneAssets,   basePath: '/assets/about/milestone/' },
    aboutBoats:   { assets: aboutBoatAssets,   basePath: '/assets/about/' },
    homeSvg:      { assets: homeSvgAssets,     basePath: '/assets/home/svg/' },
    testimonials: { assets: testimonialAssets, basePath: '/assets/testimonial-faces/' },
    icons:        { assets: iconSvgAssets,     basePath: '/assets/icons/svg/' },
    images:       { assets: imageAssets,       basePath: '/assets/images/' },
    payment:      { assets: paymentAssets,     basePath: '/assets/images/payment/' },
};

// ============================================================================
// Generic Asset Getter - Primary API
// ============================================================================

/**
 * Get an asset URL by category and filename.
 *
 * @param category - The asset category (e.g., 'partners', 'milestones', 'homeSvg', 'icons')
 * @param filename - The filename including extension (e.g., 'yachtpool.png', 'secure.svg')
 * @param fallback - Optional fallback URL if asset not found
 * @returns The resolved asset URL or fallback
 *
 * @example
 * // Partner page images
 * getAsset('partners', 'yachtpool_2025-03-10.png')
 *
 * // Partner logos (homepage)
 * getAsset('partnerLogos', 'yachtpool_2025-03-10.png')
 *
 * // Milestone images
 * getAsset('milestones', '1987.png')
 *
 * // Home SVG icons
 * getAsset('homeSvg', 'secure.svg')
 *
 * // UI icons
 * getAsset('icons', 'Arrow_left_16x16.svg')
 *
 * // Testimonial avatars (use display name with spaces, .jpg extension)
 * getAsset('testimonials', 'John-S.jpg')
 *
 * // General images
 * getAsset('images', 'boat.png')
 */
export const getAsset = (category: AssetCategory, filename: string, fallback: string = ''): string => {
    const config = assetMaps[category];
    if (!config) {
        return fallback;
    }
    const key = `${config.basePath}${filename}`;
    return config.assets[key] || fallback;
};

// ============================================================================
// Special Purpose Functions (with business logic that can't be generic)
// ============================================================================

// Boat category ID to filename mapping
const categoryImages: Record<number, string> = {
    20: 'catamaran',
    21: 'Gullet',
    45: 'motor_yacht',
    41: 'sailing_yacht',
};

/**
 * Get boat type icon by category ID.
 * This has special logic to map numeric IDs to filenames.
 */
export const getCategoryImage = (category: number | null | undefined): string => {
    const defaultIcon = getAsset('icons', 'Boat_16x16.svg');
    if (category == null) {
        return defaultIcon;
    }
    return getAsset('boatTypes', `${categoryImages[category]}.svg`, defaultIcon);
};

// Default boat image for yacht cards when no image is available
export const defaultBoatImage = getAsset('images', 'Cosmos_yachting_illustration_2.svg');
